import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import svgLogo from '../../../assets/s21.svg';
import './Navbar.css';
import Typography from '@mui/material/Typography';
import { NavLink } from 'react-router-dom';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import '../../../translations/i18n';
import { ListItemIcon } from '@mui/material';
import { useAppContext } from '../../../application/App.Context';
import UserLabel from '../../../application/User/UserLabel';
import { equivalencyRequestNavigation } from '../../../application/App.routes';

import InfoDialog from '../../InfoDialog/InfoDialog';
import { IframeHelpContent } from '../../IframeContent/IframeContent';
const drawerWidth = 300;

interface LoginButtonProps {
  children: React.ReactNode;
}

interface NavbarProps {
  window?: () => Window;
}

const LoginButton: React.FC<LoginButtonProps> = ({ children }) => {
  const { user } = useAppContext();
  if (user.valid) {
    return <UserLabel />;
  }
  return <div>{children}</div>;
};

const Navbar: React.FC<NavbarProps> = ({ window }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openTutorialDialog, setOpenTutorialDialog] = React.useState(false);

  const { t } = useTranslation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
      <List>
        <div className='logo-nav'>
          <img src={svgLogo} alt="Siglo21" />
        </div>
        <Divider />
        <ListItem >
          <ListItemButton
            component={NavLink}
            to={equivalencyRequestNavigation.equivalency()}
          >
            <ListItemIcon>
              <PlaylistAddCheckIcon />
            </ListItemIcon>
            <ListItemText primary={t('equivalencyRequestTitle')} />
          </ListItemButton>
        </ListItem>
        <ListItem>
          <ListItemButton
            component={NavLink}
            to={equivalencyRequestNavigation.searchEquivalency('')}
          >
            <ListItemIcon>
              <SearchIcon />
            </ListItemIcon>
            <ListItemText primary={t('equivalencySearchTitle')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar component="nav" color="transparent" sx={{ backgroundColor: '#FFFFFF' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, color: '#7c7c7c' }}
          >
            <MenuIcon color="inherit" />
          </IconButton>
          <div className="Nav__responsive">
            <Typography
              variant="h6"
              component="div"
              id='logo'
              sx={{ flexGrow: 1, display: { xs: 'block', sm: 'block' } }}
            >
              <img src={svgLogo} alt="Siglo21" className="Navbar__logo" />
            </Typography>
            <Typography
              variant="h6"
              component="div"
              id='site-title'
              sx={{ flexGrow: 1, display: { xs: 'block', sm: 'block' } }}
            >
              {t('equivalenceManager')}
            </Typography>
          </div>
          <Box
            className='Actions-Buttons'
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Button
              variant="text"
              sx={{ color: '#474646', gap: '0.1rem' }}
              component={NavLink}
              to={equivalencyRequestNavigation.equivalency()}
            >
              <PlaylistAddCheckIcon sx={{ p: '4px' }} />
              {t('equivalencyRequestTitle')}
            </Button>
            <Button
              variant="text"
              sx={{ color: '#474646', gap: '0.1rem' }}
              component={NavLink}
              to={equivalencyRequestNavigation.searchEquivalency('')}
            >
              <SearchIcon sx={{ p: '4px' }} />
              {t('equivalencySearchTitle')}
            </Button>
            <InfoDialog
              open={openTutorialDialog}
              title={t('infotitle')}
              content={<IframeHelpContent />}
              onClose={(e) => {
                e.stopPropagation()
                setOpenTutorialDialog(false)
              }}
              size='lg'
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Box component="nav">
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main">
        <Toolbar />
      </Box>
    </Box >
  );
};

export default Navbar;
